import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    BellRing,
    CalendarDays,
    Clock,
    Filter,
    LayoutGrid,
    List,
    Loader2,
    MapPin,
    Plus,
    RefreshCw,
    Search,
    User,
    X,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import CalendarContextMenu from "../components/calendar/CalendarContextMenu";
import CalendarDefter from "../components/calendar/CalendarDefter";
import CalendarEventModal from "../components/calendar/CalendarEventModal";
import { Button } from "../components/ui/button";
import { crud, takvimMerkez } from "../lib/api";
import {
    defaultEtkinlikForm,
    defaultHatirlatmaForm,
    defaultRandevuForm,
    loadFormFromEvent,
    payloadFromForm,
} from "../lib/calendarForms";
import {
    ETKINLIK_TUR_STYLES,
    HOURS,
    ONEM_STYLES,
    SOURCE_STYLES,
    WEEKDAYS,
    addDays,
    addMonths,
    endOfMonth,
    endOfWeek,
    eventBadgeLabel,
    eventStyle,
    eventsInHour,
    eventsOnDay,
    filterEvents,
    formatDateLong,
    formatDateShort,
    formatTime,
    isSameDay,
    isToday,
    normalizeApiEvent,
    parseApiDate,
    rangeForView,
    startOfMonth,
    startOfWeek,
    toYmd,
} from "../lib/calendarUtils";

const VIEWS = [
    { id: "month", label: "Ay", icon: LayoutGrid },
    { id: "week", label: "Hafta", icon: CalendarDays },
    { id: "day", label: "Gün", icon: Clock },
    { id: "agenda", label: "Ajanda", icon: List },
];

const DEFAULT_SOURCES = new Set(["randevu", "hatirlatma", "takvim"]);

export default function CalendarPage() {
    const [view, setView] = useState("month");
    const [cursor, setCursor] = useState(() => new Date());
    const [selectedDay, setSelectedDay] = useState(() => toYmd(new Date()));
    const [events, setEvents] = useState([]);
    const [ozet, setOzet] = useState(null);
    const [upcoming, setUpcoming] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [sources, setSources] = useState(DEFAULT_SOURCES);
    const [etkinlikTuru, setEtkinlikTuru] = useState("");
    const [durum, setDurum] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [ctxMenu, setCtxMenu] = useState(null);
    const [pageTurn, setPageTurn] = useState(null);
    const turnTimer = useRef(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState("create");
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    const range = useMemo(() => rangeForView(view, cursor), [view, cursor]);
    const pageNumber = cursor.getFullYear() * 12 + cursor.getMonth() + 1;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const sourcesArr = Array.from(sources).join(",");
            const [data, up] = await Promise.all([
                takvimMerkez.list({
                    from: range.from,
                    to: range.to,
                    sources: sourcesArr,
                    q: search.trim() || undefined,
                    etkinlik_turu: etkinlikTuru || undefined,
                    durum: durum || undefined,
                }),
                takvimMerkez.yaklasan(),
            ]);
            setEvents((data.events || []).map(normalizeApiEvent).filter(Boolean));
            setOzet(data.ozet || null);
            setUpcoming((up.events || []).map(normalizeApiEvent).filter(Boolean));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Takvim yüklenemedi.");
            setEvents([]);
        } finally {
            setLoading(false);
        }
    }, [range.from, range.to, sources, search, etkinlikTuru, durum]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        return () => {
            if (turnTimer.current) clearTimeout(turnTimer.current);
        };
    }, []);

    const filtered = useMemo(
        () => filterEvents(events, { sources, search, etkinlikTuru, durum }),
        [events, sources, search, etkinlikTuru, durum],
    );

    const selectedDayEvents = useMemo(
        () => eventsOnDay(filtered, selectedDay).sort((a, b) => a.start - b.start),
        [filtered, selectedDay],
    );

    const headerLabel = useMemo(() => {
        if (view === "day") return formatDateLong(cursor);
        if (view === "week") {
            const s = startOfWeek(cursor);
            const e = endOfWeek(cursor);
            return `${formatDateShort(s)} – ${formatDateShort(e)} ${e.getFullYear()}`;
        }
        return cursor.toLocaleDateString("tr-TR", { month: "long", year: "numeric" });
    }, [view, cursor]);

    const monthLabel = cursor.toLocaleDateString("tr-TR", { month: "long", year: "numeric" });

    function triggerPageTurn(dir, fn) {
        if (view === "month") {
            setPageTurn(dir === 1 ? "next" : "prev");
            if (turnTimer.current) clearTimeout(turnTimer.current);
            turnTimer.current = setTimeout(() => {
                fn();
                setTimeout(() => setPageTurn(null), 80);
            }, 280);
        } else {
            fn();
        }
    }

    function goToday() {
        const t = new Date();
        setCursor(t);
        setSelectedDay(toYmd(t));
    }

    function navigate(dir) {
        triggerPageTurn(dir, () => {
            if (view === "month") setCursor((c) => addMonths(c, dir));
            else if (view === "week") setCursor((c) => addDays(c, dir * 7));
            else if (view === "day") setCursor((c) => addDays(c, dir));
            else goToday();
        });
    }

    function openContextMenu(e, payload) {
        e.preventDefault();
        e.stopPropagation();
        setCtxMenu({
            x: Math.min(e.clientX, window.innerWidth - 220),
            y: Math.min(e.clientY, window.innerHeight - 200),
            ...payload,
        });
    }

    function openCreate(tip, dayKey, hour = 10) {
        const base = dayKey || selectedDay || toYmd(new Date());
        let f;
        if (tip === "hatirlatma") f = defaultHatirlatmaForm(base, hour);
        else if (tip === "takvim") f = defaultEtkinlikForm(base, hour);
        else f = defaultRandevuForm(base, hour);
        setForm(f);
        setModalMode("create");
        setModalOpen(true);
    }

    async function openEdit(event) {
        setSaving(true);
        const loaded = await loadFormFromEvent(crud, event);
        setSaving(false);
        if (loaded) {
            setForm(loaded);
            setModalMode("edit");
            setModalOpen(true);
        } else {
            toast.error("Kayıt yüklenemedi.");
        }
    }

    async function handleContextAction(action, menu) {
        if (action === "add-randevu") openCreate("randevu", menu.dayKey, menu.hour);
        if (action === "add-hatirlatma") openCreate("hatirlatma", menu.dayKey, menu.hour);
        if (action === "add-etkinlik") openCreate("takvim", menu.dayKey, menu.hour);
        if (action === "view-day") {
            setSelectedDay(menu.dayKey);
            setCursor(parseApiDate(menu.dayKey + "T12:00:00") || new Date());
            setView("day");
        }
        if (action === "view-agenda") setView("agenda");
        if (action === "view-event" && menu.event) setSelectedEvent(menu.event);
        if (action === "edit-event" && menu.event) openEdit(menu.event);
        if (action === "delete-event" && menu.event) {
            if (!window.confirm("Bu kayıt silinsin mi?")) return;
            try {
                await takvimMerkez.sil(menu.event.source, menu.event.recordId);
                toast.success("Silindi.");
                load();
            } catch {
                toast.error("Silinemedi.");
            }
        }
    }

    async function handleModalSubmit(e) {
        e.preventDefault();
        if (!form?.baslik && form?.tip !== "hatirlatma") {
            if (!form.baslik?.trim()) {
                toast.error("Başlık zorunlu.");
                return;
            }
        }
        setSaving(true);
        try {
            const payload = payloadFromForm(form);
            if (modalMode === "edit" && form.id) {
                await takvimMerkez.guncelle(form.tip, form.id, payload);
                toast.success("Güncellendi.");
            } else {
                await takvimMerkez.olustur(payload);
                toast.success("Kaydedildi.");
            }
            setModalOpen(false);
            setForm(null);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "İşlem başarısız.");
        } finally {
            setSaving(false);
        }
    }

    async function handleModalDelete() {
        if (!form?.id || !window.confirm("Silinsin mi?")) return;
        setSaving(true);
        try {
            await takvimMerkez.sil(form.tip, form.id);
            toast.success("Silindi.");
            setModalOpen(false);
            load();
        } catch {
            toast.error("Silinemedi.");
        } finally {
            setSaving(false);
        }
    }

    const ctxHandlers = { onContextMenu: openContextMenu };

    const sidebar = (
        <>
            <SidebarKpi ozet={ozet} />
            <SidebarDayPanel
                dayKey={selectedDay}
                events={selectedDayEvents}
                onEventClick={setSelectedEvent}
                onContextMenu={openContextMenu}
                onAddRandevu={() => openCreate("randevu", selectedDay)}
            />
            <SidebarUpcoming events={upcoming} onEventClick={setSelectedEvent} onContextMenu={openContextMenu} />
        </>
    );

    return (
        <div className="p-2 md:p-4 max-w-[1800px] mx-auto space-y-4 animate-fade-in">
            {/* Üst kontrol — defter dışı */}
            <div className="flex flex-wrap items-center gap-2 px-2">
                <div className="flex rounded-lg border border-white/10 overflow-hidden bg-[#121A2F]">
                    {VIEWS.map((v) => {
                        const Icon = v.icon;
                        return (
                            <button
                                key={v.id}
                                type="button"
                                onClick={() => setView(v.id)}
                                className={`px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 ${
                                    view === v.id
                                        ? "bg-[rgba(37,211,102,0.15)] text-[#25d366]"
                                        : "text-[#8b93ae] hover:bg-white/5"
                                }`}
                            >
                                <Icon className="h-3.5 w-3.5" />
                                {v.label}
                            </button>
                        );
                    })}
                </div>
                <div className="relative flex-1 min-w-[160px] max-w-xs">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8b93ae]" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Ara…"
                        className="w-full pl-9 pr-3 py-1.5 rounded-lg border border-white/10 bg-[#121A2F] text-sm"
                    />
                </div>
                <Button size="sm" onClick={() => openCreate("randevu")} className="bg-[#25d366] text-white">
                    <Plus className="h-4 w-4 mr-1" />
                    Ekle
                </Button>
                <Button size="sm" variant="outline" onClick={load} disabled={loading}>
                    <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                </Button>
                <button
                    type="button"
                    onClick={() => setShowFilters((s) => !s)}
                    className={`px-3 py-1.5 rounded-lg border text-xs inline-flex items-center gap-1 ${
                        showFilters ? "border-[#c9a961]/40 text-[#c9a961]" : "border-white/10 text-[#8b93ae]"
                    }`}
                >
                    <Filter className="h-3.5 w-3.5" />
                </button>
            </div>

            {showFilters && (
                <div className="mx-2 rounded-xl border border-white/10 bg-[#121A2F] p-4 flex flex-wrap gap-4 text-sm">
                    <FilterSources sources={sources} setSources={setSources} />
                    <select
                        value={etkinlikTuru}
                        onChange={(e) => setEtkinlikTuru(e.target.value)}
                        className="rounded-lg border border-white/10 bg-[#0b1437] px-3 py-1.5 text-xs"
                    >
                        <option value="">Tüm etkinlik türleri</option>
                        {Object.entries(ETKINLIK_TUR_STYLES).map(([k, v]) => (
                            <option key={k} value={k}>
                                {v.label}
                            </option>
                        ))}
                    </select>
                    <select
                        value={durum}
                        onChange={(e) => setDurum(e.target.value)}
                        className="rounded-lg border border-white/10 bg-[#0b1437] px-3 py-1.5 text-xs"
                    >
                        <option value="">Tüm randevu durumları</option>
                        <option value="planlandi">Planlandı</option>
                        <option value="tamamlandi">Tamamlandı</option>
                        <option value="iptal">İptal</option>
                    </select>
                </div>
            )}

            <CalendarDefter
                monthLabel={monthLabel}
                pageNumber={pageNumber}
                pageTurn={pageTurn}
                onPrevPage={() => navigate(-1)}
                onNextPage={() => navigate(1)}
                sidebar={sidebar}
            >
                <div className="mb-3 flex flex-wrap gap-2 text-[10px]">
                    {Object.entries(SOURCE_STYLES).map(([k, st]) => (
                        <span key={k} className={`px-2 py-0.5 rounded-full border ${st.className}`}>
                            {st.label}
                        </span>
                    ))}
                    <span className="ml-auto text-[#8b7355]">{filtered.length} kayıt</span>
                </div>

                {loading ? (
                    <div className="h-80 flex items-center justify-center">
                        <Loader2 className="h-10 w-10 animate-spin text-[#8b6914]" />
                    </div>
                ) : (
                    <>
                        {view === "month" && (
                            <MonthView
                                cursor={cursor}
                                events={filtered}
                                selectedDay={selectedDay}
                                onSelectDay={setSelectedDay}
                                onEventClick={setSelectedEvent}
                                ctx={ctxHandlers}
                            />
                        )}
                        {view === "week" && (
                            <WeekView
                                cursor={cursor}
                                events={filtered}
                                selectedDay={selectedDay}
                                onSelectDay={setSelectedDay}
                                onEventClick={setSelectedEvent}
                                ctx={ctxHandlers}
                            />
                        )}
                        {view === "day" && (
                            <DayView cursor={cursor} events={filtered} onEventClick={setSelectedEvent} ctx={ctxHandlers} />
                        )}
                        {view === "agenda" && (
                            <AgendaView events={filtered} onEventClick={setSelectedEvent} ctx={ctxHandlers} />
                        )}
                    </>
                )}
            </CalendarDefter>

            <CalendarContextMenu menu={ctxMenu} onAction={handleContextAction} onClose={() => setCtxMenu(null)} />

            {selectedEvent && (
                <EventDrawer
                    event={selectedEvent}
                    onClose={() => setSelectedEvent(null)}
                    onEdit={() => {
                        openEdit(selectedEvent);
                        setSelectedEvent(null);
                    }}
                    onDelete={async () => {
                        if (!window.confirm("Silinsin mi?")) return;
                        await takvimMerkez.sil(selectedEvent.source, selectedEvent.recordId);
                        toast.success("Silindi.");
                        setSelectedEvent(null);
                        load();
                    }}
                />
            )}

            <CalendarEventModal
                open={modalOpen}
                mode={modalMode}
                form={form}
                setForm={setForm}
                saving={saving}
                onClose={() => {
                    setModalOpen(false);
                    setForm(null);
                }}
                onSubmit={handleModalSubmit}
                onDelete={modalMode === "edit" ? handleModalDelete : undefined}
            />
        </div>
    );
}

function FilterSources({ sources, setSources }) {
    return (
        <div className="flex flex-wrap gap-2">
            {Object.entries(SOURCE_STYLES).map(([key, st]) => (
                <button
                    key={key}
                    type="button"
                    onClick={() =>
                        setSources((prev) => {
                            const next = new Set(prev);
                            if (next.has(key)) {
                                if (next.size > 1) next.delete(key);
                            } else next.add(key);
                            return next;
                        })
                    }
                    className={`px-2 py-1 rounded-full text-xs border ${
                        sources.has(key) ? st.className : "opacity-40 border-white/10"
                    }`}
                >
                    {st.label}
                </button>
            ))}
        </div>
    );
}

function MonthView({ cursor, events, selectedDay, onSelectDay, onEventClick, ctx }) {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const startPad = (first.getDay() + 6) % 7;
    const cells = [];
    for (let i = 0; i < startPad; i++) cells.push(null);
    for (let day = 1; day <= last.getDate(); day++) {
        const date = new Date(year, month, day);
        cells.push({ date, key: toYmd(date), day, events: eventsOnDay(events, toYmd(date)) });
    }

    return (
        <div>
            <div className="grid grid-cols-7 gap-1 mb-2">
                {WEEKDAYS.map((w) => (
                    <div key={w} className="text-center text-[10px] uppercase font-bold text-[#8b6914] py-1">
                        {w}
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
                {cells.map((cell, idx) => {
                    if (!cell) {
                        return <div key={`e-${idx}`} className="min-h-[96px] rounded bg-[#ebe4d6]/40" />;
                    }
                    const today = isToday(cell.date);
                    const selected = selectedDay === cell.key;
                    return (
                        <div
                            key={cell.key}
                            role="button"
                            tabIndex={0}
                            onClick={() => onSelectDay(cell.key)}
                            onKeyDown={(e) => e.key === "Enter" && onSelectDay(cell.key)}
                            onContextMenu={(e) =>
                                ctx.onContextMenu(e, { type: "day", dayKey: cell.key, hour: 10 })
                            }
                            className={`min-h-[96px] rounded border p-1.5 cursor-pointer transition ${
                                selected
                                    ? "border-[#c9a961] bg-[#f5ecd6]/80 shadow-inner"
                                    : "border-[#c9a961]/20 bg-white/50 hover:border-[#c9a961]/45"
                            } ${today ? "ring-2 ring-emerald-600/40" : ""}`}
                        >
                            <span
                                className={`text-xs font-bold font-mono ${
                                    today ? "text-emerald-700" : "text-[#8b7355]"
                                }`}
                            >
                                {cell.day}
                            </span>
                            <div className="mt-1 space-y-0.5">
                                {cell.events.slice(0, 3).map((ev) => {
                                    const st = eventStyle(ev);
                                    return (
                                        <button
                                            key={ev.id}
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onEventClick(ev);
                                            }}
                                            onContextMenu={(e) =>
                                                ctx.onContextMenu(e, { type: "event", event: ev, dayKey: cell.key })
                                            }
                                            className={`block w-full truncate text-left text-[9px] px-1 py-0.5 rounded border ${st.className}`}
                                        >
                                            {!ev.all_day && (
                                                <span className="opacity-70">{formatTime(ev.start)} </span>
                                            )}
                                            {ev.title}
                                        </button>
                                    );
                                })}
                                {cell.events.length > 3 && (
                                    <span className="text-[9px] text-[#8b7355]">+{cell.events.length - 3}</span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function WeekView({ cursor, events, selectedDay, onSelectDay, onEventClick, ctx }) {
    const weekStart = startOfWeek(cursor);
    const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    return (
        <div className="overflow-x-auto rounded border border-[#c9a961]/25 bg-white/40">
            <div className="grid grid-cols-8 border-b border-[#c9a961]/20 min-w-[640px]">
                <div className="p-2 text-[10px] text-[#8b7355]" />
                {days.map((d) => {
                    const key = toYmd(d);
                    return (
                        <button
                            key={key}
                            type="button"
                            onClick={() => onSelectDay(key)}
                            onContextMenu={(e) => ctx.onContextMenu(e, { type: "day", dayKey: key, hour: 10 })}
                            className={`p-2 text-center border-l border-[#c9a961]/15 ${
                                selectedDay === key ? "bg-[#f5ecd6]" : ""
                            }`}
                        >
                            <div className="text-[10px] text-[#8b7355]">{WEEKDAYS[(d.getDay() + 6) % 7]}</div>
                            <div className={`text-lg font-display ${isToday(d) ? "text-emerald-700" : "text-[#1a2744]"}`}>
                                {d.getDate()}
                            </div>
                        </button>
                    );
                })}
            </div>
            <div className="max-h-[480px] overflow-y-auto min-w-[640px]">
                {HOURS.map((hour) => (
                    <div key={hour} className="grid grid-cols-8 border-b border-[#c9a961]/10 min-h-[44px]">
                        <div className="p-1 text-[10px] text-[#8b7355] font-mono border-r border-[#c9a961]/15">
                            {String(hour).padStart(2, "0")}:00
                        </div>
                        {days.map((d) => {
                            const key = toYmd(d);
                            return (
                                <div
                                    key={`${key}-${hour}`}
                                    className="border-l border-[#c9a961]/10 p-0.5"
                                    onContextMenu={(e) =>
                                        ctx.onContextMenu(e, { type: "day", dayKey: key, hour })
                                    }
                                >
                                    {eventsInHour(events, key, hour).map((ev) => (
                                        <EventChip
                                            key={ev.id}
                                            ev={ev}
                                            onClick={() => onEventClick(ev)}
                                            onContextMenu={(e) =>
                                                ctx.onContextMenu(e, { type: "event", event: ev, dayKey: key })
                                            }
                                        />
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
}

function DayView({ cursor, events, onEventClick, ctx }) {
    const key = toYmd(cursor);
    return (
        <div
            onContextMenu={(e) => ctx.onContextMenu(e, { type: "day", dayKey: key, hour: 10 })}
        >
            <h2 className="text-sm font-semibold text-[#1a2744] mb-3">{formatDateLong(cursor)}</h2>
            {HOURS.map((hour) => (
                <div
                    key={hour}
                    className="flex gap-3 min-h-[48px] border-b border-[#c9a961]/15 py-1"
                    onContextMenu={(e) => ctx.onContextMenu(e, { type: "day", dayKey: key, hour })}
                >
                    <span className="w-12 text-[10px] text-[#8b7355] font-mono">{String(hour).padStart(2, "0")}:00</span>
                    <div className="flex-1 space-y-1">
                        {eventsInHour(events, key, hour).map((ev) => (
                            <EventChip
                                key={ev.id}
                                ev={ev}
                                full
                                onClick={() => onEventClick(ev)}
                                onContextMenu={(e) =>
                                    ctx.onContextMenu(e, { type: "event", event: ev, dayKey: key })
                                }
                            />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

function AgendaView({ events, onEventClick, ctx }) {
    const grouped = useMemo(() => {
        const map = new Map();
        [...events].sort((a, b) => a.start - b.start).forEach((ev) => {
            const k = ev.dayKeys?.[0] || toYmd(ev.start);
            if (!map.has(k)) map.set(k, []);
            map.get(k).push(ev);
        });
        return Array.from(map.entries());
    }, [events]);

    if (!grouped.length) {
        return <p className="text-center text-[#8b7355] py-8">Ajanda boş.</p>;
    }

    return (
        <div className="space-y-4">
            {grouped.map(([dayKey, dayEvents]) => (
                <div
                    key={dayKey}
                    onContextMenu={(e) => ctx.onContextMenu(e, { type: "day", dayKey, hour: 10 })}
                >
                    <h3 className="text-xs font-bold text-[#8b6914] mb-2">
                        {formatDateLong(parseApiDate(dayKey + "T12:00:00"))}
                    </h3>
                    <ul className="space-y-2">
                        {dayEvents.map((ev) => (
                            <li key={ev.id}>
                                <EventCard
                                    ev={ev}
                                    onClick={() => onEventClick(ev)}
                                    onContextMenu={(e) =>
                                        ctx.onContextMenu(e, { type: "event", event: ev, dayKey })
                                    }
                                />
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    );
}

function EventChip({ ev, onClick, onContextMenu, full }) {
    const st = eventStyle(ev);
    const onem = ev.meta?.onem ? ONEM_STYLES[ev.meta.onem] : "";
    return (
        <button
            type="button"
            onClick={onClick}
            onContextMenu={onContextMenu}
            className={`text-left w-full rounded border px-1.5 py-1 ${st.className} border-l-2 ${onem} ${
                full ? "text-xs" : "text-[9px]"
            }`}
        >
            <span className="font-mono opacity-70">{formatTime(ev.start)}</span> {ev.title}
        </button>
    );
}

function EventCard({ ev, onClick, onContextMenu }) {
    const st = eventStyle(ev);
    return (
        <button
            type="button"
            onClick={onClick}
            onContextMenu={onContextMenu}
            className={`w-full text-left rounded-lg border p-3 ${st.className}`}
        >
            <div className="flex justify-between text-[10px] uppercase opacity-80">
                <span>{eventBadgeLabel(ev)}</span>
                <span className="font-mono">{formatTime(ev.start)}</span>
            </div>
            <div className="text-sm font-medium mt-1">{ev.title}</div>
        </button>
    );
}

function SidebarKpi({ ozet }) {
    return (
        <div className="grid grid-cols-2 gap-2">
            {[
                ["Bugün", ozet?.bugun, "text-emerald-300"],
                ["Hafta", ozet?.bu_hafta, "text-sky-300"],
                ["Geciken", ozet?.geciken_hatirlatma, "text-rose-300"],
                ["Toplam", ozet?.toplam, "text-[#F3E5AB]"],
            ].map(([l, v, c]) => (
                <div key={l} className="rounded-lg border border-[#c9a961]/20 bg-black/30 px-2 py-1.5">
                    <div className="text-[9px] text-[#8b93ae] uppercase">{l}</div>
                    <div className={`text-lg font-display ${c}`}>{v ?? "—"}</div>
                </div>
            ))}
        </div>
    );
}

function SidebarDayPanel({ dayKey, events, onEventClick, onContextMenu, onAddRandevu }) {
    return (
        <div className="rounded-lg border border-[#c9a961]/20 bg-black/25 p-3">
            <div className="flex justify-between items-center mb-2">
                <h3 className="text-xs font-semibold text-[#f5ecd6] flex items-center gap-1">
                    <BellRing className="h-3.5 w-3.5 text-[#c9a961]" />
                    Seçili gün
                </h3>
                <button type="button" onClick={onAddRandevu} className="text-[10px] text-[#25d366]">
                    + Ekle
                </button>
            </div>
            <p className="text-[10px] text-[#8b93ae] mb-2 capitalize">
                {dayKey ? formatDateShort(parseApiDate(dayKey + "T12:00:00")) : "—"}
            </p>
            <ul className="space-y-1 max-h-[160px] overflow-y-auto">
                {events.map((ev) => (
                    <li key={ev.id}>
                        <button
                            type="button"
                            onClick={() => onEventClick(ev)}
                            onContextMenu={(e) => onContextMenu(e, { type: "event", event: ev, dayKey })}
                            className="w-full text-left text-[10px] text-[#c5cad8] hover:text-white truncate py-0.5"
                        >
                            {formatTime(ev.start)} · {ev.title}
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function SidebarUpcoming({ events, onEventClick, onContextMenu }) {
    return (
        <div className="rounded-lg border border-[#c9a961]/20 bg-black/25 p-3">
            <h3 className="text-xs font-semibold text-[#f5ecd6] mb-2 flex items-center gap-1">
                <Clock className="h-3.5 w-3.5 text-sky-400" />
                Yaklaşan
            </h3>
            <ul className="space-y-1 max-h-[140px] overflow-y-auto">
                {events.slice(0, 8).map((ev) => (
                    <li key={ev.id}>
                        <button
                            type="button"
                            onClick={() => onEventClick(ev)}
                            onContextMenu={(e) =>
                                onContextMenu(e, {
                                    type: "event",
                                    event: ev,
                                    dayKey: ev.dayKeys?.[0],
                                })
                            }
                            className="w-full text-left text-[10px] text-[#8b93ae] hover:text-[#f5ecd6] truncate"
                        >
                            {formatDateShort(ev.start)} {ev.title}
                        </button>
                    </li>
                ))}
            </ul>
            <div className="mt-3 flex flex-wrap gap-2 text-[10px]">
                <Link to="/e/randevular" className="text-[#c9a961] hover:underline">
                    Randevular
                </Link>
                <Link to="/e/hatirlatmalar" className="text-[#c9a961] hover:underline">
                    Hatırlatmalar
                </Link>
            </div>
        </div>
    );
}

function EventDrawer({ event, onClose, onEdit, onDelete }) {
    const st = eventStyle(event);
    const links = event.links || {};
    return (
        <div className="fixed inset-0 z-[9990] flex justify-end">
            <button type="button" className="absolute inset-0 bg-black/60" onClick={onClose} aria-label="Kapat" />
            <div className="relative w-full max-w-md bg-[#faf6ee] text-[#1a2744] border-l-4 border-[#c9a961] shadow-2xl overflow-y-auto">
                <div className="sticky top-0 flex items-center justify-between p-4 border-b border-[#c9a961]/30 bg-[#f5ecd6]">
                    <span className={`text-[10px] uppercase px-2 py-0.5 rounded border ${st.className}`}>
                        {eventBadgeLabel(event)}
                    </span>
                    <button type="button" onClick={onClose}>
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="p-5 space-y-4">
                    <h2 className="font-display text-xl">{event.title}</h2>
                    <p className="text-sm text-[#8b7355]">
                        {formatDateLong(event.start)} · {formatTime(event.start)}
                    </p>
                    {event.meta?.konum && (
                        <p className="flex items-center gap-1 text-sm">
                            <MapPin className="h-4 w-4" /> {event.meta.konum}
                        </p>
                    )}
                    {event.meta?.muvekkil_adi && (
                        <p className="flex items-center gap-1 text-sm">
                            <User className="h-4 w-4" /> {event.meta.muvekkil_adi}
                        </p>
                    )}
                    {event.description && (
                        <p className="text-sm whitespace-pre-wrap border-l-2 border-[#c9a961]/40 pl-3">
                            {event.description}
                        </p>
                    )}
                    <div className="flex flex-wrap gap-2">
                        <Button size="sm" onClick={onEdit} className="bg-[#1a2744] text-white">
                            Düzenle
                        </Button>
                        <Button size="sm" variant="outline" className="text-rose-600" onClick={onDelete}>
                            Sil
                        </Button>
                        {links.dosya && (
                            <Link to={links.dosya} className="text-xs text-sky-700 underline self-center">
                                Dosya →
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
